/**
 * PlayArt Component
 * Displays route lines for offense and zone coverage for defense
 * Shows before simulation plays to visualize the play design
 * Draws connector lines from each defender to their assigned zone
 */

import React from 'react';
import { RouteType, RBRouteType, CoverageType, FormationType, Waypoint, CustomDefenseAssignments, DefensePositionId } from '../types';
import { getRouteWaypoints, getRBRouteWaypoints } from '../rules/routeRules';
import {
  getCB1StartPosition,
  getCB2StartPosition,
  getDBLB1StartPosition,
  getDBLB2StartPosition,
  getFSStartPosition,
  getSSStartPosition,
  getLBStartPosition,
  DEFENSE_POSITION_IDS,
} from '../rules';
import './PlayArt.css';

interface ZoneDefinition {
  cx: number;
  cy: number;
  rx: number;
  ry: number;
  color: string;
  opacity: number;
  label?: string;
}

/** A connector links a defender's start position to a zone center */
interface ZoneConnector {
  zone: ZoneDefinition;
  /** Player color from AnimationEngine palette */
  playerColor: string;
  /** Player position key to look up start coords */
  getStart: (coverage: CoverageType, formation: FormationType) => { x: number; y: number };
  /** Short label for the player */
  tag: string;
}

/* ── Zone type colors ──
 *
 * Read from the design tokens rather than restated as literals, so the play art, the player glyphs
 * and the legend cannot drift apart. They previously did: this file's deep blue was #4464ff while
 * the field's corner was #4444ff and PlayArt.css's zone oval was rgba(100,140,255,...) — three
 * near-misses for one concept.
 *
 * The tokens follow the coverage LEVEL, which is also how the defenders are now coloured:
 *   deep zones        -> --color-def-deep
 *   underneath zones  -> --color-def-underneath
 *
 * `var()` works here because these values are only ever used as SVG `stroke`/`fill` attribute
 * values on elements inside the document, where custom properties resolve normally.
 */
const DEEP = 'var(--color-def-deep)';
const CURL_FLAT = 'var(--color-def-underneath)';
const HOOK = 'var(--color-turf-chalk)';
const FLAT = 'var(--color-def-deep)';

const DEEP_LINE = 'var(--color-def-deep)';
const CURL_FLAT_LINE = 'var(--color-def-underneath)';
const HOOK_LINE = 'var(--color-turf-chalk)';
const FLAT_LINE = 'var(--color-def-deep)';

/* ── Cover 4 zone-to-player mappings ── */
const COVER4_CONNECTORS: ZoneConnector[] = [
  // Deep quarters
  { zone: { cx: 12, cy: 22, rx: 11, ry: 10, color: DEEP, opacity: 1, label: '¼' }, playerColor: DEEP_LINE, getStart: (c, f) => getCB1StartPosition(c, f), tag: 'CB1' },
  { zone: { cx: 35, cy: 22, rx: 11, ry: 10, color: DEEP, opacity: 1, label: '¼' }, playerColor: DEEP_LINE, getStart: (c, f) => getFSStartPosition(c, f), tag: 'FS' },
  { zone: { cx: 65, cy: 22, rx: 11, ry: 10, color: DEEP, opacity: 1, label: '¼' }, playerColor: DEEP_LINE, getStart: (c, f) => getSSStartPosition(c, f), tag: 'SS' },
  { zone: { cx: 88, cy: 22, rx: 11, ry: 10, color: DEEP, opacity: 1, label: '¼' }, playerColor: DEEP_LINE, getStart: (c, f) => getCB2StartPosition(c, f), tag: 'CB2' },
  // Curl/flat underneath
  { zone: { cx: 30, cy: 55, rx: 10, ry: 7, color: CURL_FLAT, opacity: 1 }, playerColor: CURL_FLAT_LINE, getStart: (c, f) => getDBLB1StartPosition(c, f), tag: 'DB1' },
  // Hook zone
  { zone: { cx: 50, cy: 50, rx: 12, ry: 8, color: HOOK, opacity: 1 }, playerColor: HOOK_LINE, getStart: (c, f) => getLBStartPosition(c, f), tag: 'LB' },
  // Curl/flat underneath
  { zone: { cx: 70, cy: 55, rx: 10, ry: 7, color: CURL_FLAT, opacity: 1 }, playerColor: CURL_FLAT_LINE, getStart: (c, f) => getDBLB2StartPosition(c, f), tag: 'DB2' },
];

/* ── Cover 2 zone-to-player mappings ── */
const COVER2_CONNECTORS: ZoneConnector[] = [
  // Deep halves
  { zone: { cx: 25, cy: 18, rx: 22, ry: 14, color: DEEP, opacity: 1, label: '½' }, playerColor: DEEP_LINE, getStart: (c, f) => getFSStartPosition(c, f), tag: 'FS' },
  { zone: { cx: 75, cy: 18, rx: 22, ry: 14, color: DEEP, opacity: 1, label: '½' }, playerColor: DEEP_LINE, getStart: (c, f) => getSSStartPosition(c, f), tag: 'SS' },
  // Flat zones (CB)
  { zone: { cx: 8, cy: 55, rx: 8, ry: 6, color: FLAT, opacity: 1 }, playerColor: FLAT_LINE, getStart: (c, f) => getCB1StartPosition(c, f), tag: 'CB1' },
  // Curl/flat (DB/LB)
  { zone: { cx: 28, cy: 52, rx: 10, ry: 7, color: CURL_FLAT, opacity: 1 }, playerColor: CURL_FLAT_LINE, getStart: (c, f) => getDBLB1StartPosition(c, f), tag: 'DB1' },
  // Hook zone
  { zone: { cx: 50, cy: 45, rx: 12, ry: 9, color: HOOK, opacity: 1 }, playerColor: HOOK_LINE, getStart: (c, f) => getLBStartPosition(c, f), tag: 'LB' },
  // Curl/flat (DB/LB)
  { zone: { cx: 72, cy: 52, rx: 10, ry: 7, color: CURL_FLAT, opacity: 1 }, playerColor: CURL_FLAT_LINE, getStart: (c, f) => getDBLB2StartPosition(c, f), tag: 'DB2' },
  // Flat zones (CB)
  { zone: { cx: 92, cy: 55, rx: 8, ry: 6, color: FLAT, opacity: 1 }, playerColor: FLAT_LINE, getStart: (c, f) => getCB2StartPosition(c, f), tag: 'CB2' },
];

/* ── Man coverage zone-to-player mappings ── */
const MAN_CONNECTORS: ZoneConnector[] = [
  { zone: { cx: 25, cy: 18, rx: 22, ry: 14, color: DEEP, opacity: 1, label: 'HELP' }, playerColor: DEEP_LINE, getStart: (c, f) => getFSStartPosition(c, f), tag: 'FS' },
  { zone: { cx: 75, cy: 18, rx: 22, ry: 14, color: DEEP, opacity: 1, label: 'HELP' }, playerColor: DEEP_LINE, getStart: (c, f) => getSSStartPosition(c, f), tag: 'SS' },
];

/* ── Cover 3 Match zone-to-player mappings ── */
const COVER3_CONNECTORS: ZoneConnector[] = [
  // Deep thirds
  { zone: { cx: 17, cy: 20, rx: 15, ry: 12, color: DEEP, opacity: 1 }, playerColor: DEEP_LINE, getStart: (c, f) => getCB1StartPosition(c, f), tag: 'CB1' },
  { zone: { cx: 50, cy: 18, rx: 15, ry: 12, color: DEEP, opacity: 1 }, playerColor: DEEP_LINE, getStart: (c, f) => getFSStartPosition(c, f), tag: 'FS' },
  { zone: { cx: 83, cy: 20, rx: 15, ry: 12, color: DEEP, opacity: 1 }, playerColor: DEEP_LINE, getStart: (c, f) => getCB2StartPosition(c, f), tag: 'CB2' },
  // Curl/flat left
  { zone: { cx: 25, cy: 55, rx: 10, ry: 7, color: CURL_FLAT, opacity: 1 }, playerColor: CURL_FLAT_LINE, getStart: (c, f) => getDBLB1StartPosition(c, f), tag: 'DB1' },
  // Hook left
  { zone: { cx: 42, cy: 50, rx: 10, ry: 7, color: HOOK, opacity: 1 }, playerColor: HOOK_LINE, getStart: (c, f) => getLBStartPosition(c, f), tag: 'LB' },
  // Hook right
  { zone: { cx: 58, cy: 50, rx: 10, ry: 7, color: HOOK, opacity: 1 }, playerColor: HOOK_LINE, getStart: (c, f) => getDBLB2StartPosition(c, f), tag: 'DB2' },
  // Curl/flat right
  { zone: { cx: 75, cy: 55, rx: 10, ry: 7, color: CURL_FLAT, opacity: 1 }, playerColor: CURL_FLAT_LINE, getStart: (c, f) => getSSStartPosition(c, f), tag: 'SS' },
];

/* ── Cover 6 zone-to-player mappings ──
 * Left side = Cover 4 Match, Right side = Cover 2
 */
const COVER6_CONNECTORS: ZoneConnector[] = [
  // Left side - Cover 4 deep quarters
  { zone: { cx: 12, cy: 22, rx: 11, ry: 10, color: DEEP, opacity: 1 }, playerColor: DEEP_LINE, getStart: (c, f) => getCB1StartPosition(c, f), tag: 'CB1' },
  { zone: { cx: 35, cy: 22, rx: 11, ry: 10, color: DEEP, opacity: 1 }, playerColor: DEEP_LINE, getStart: (c, f) => getFSStartPosition(c, f), tag: 'FS' },
  // Left side - curl/flat underneath
  { zone: { cx: 25, cy: 55, rx: 10, ry: 7, color: CURL_FLAT, opacity: 1 }, playerColor: CURL_FLAT_LINE, getStart: (c, f) => getDBLB1StartPosition(c, f), tag: 'DB1' },
  // Right side - Cover 2 deep half
  { zone: { cx: 75, cy: 20, rx: 22, ry: 14, color: DEEP, opacity: 1 }, playerColor: DEEP_LINE, getStart: (c, f) => getSSStartPosition(c, f), tag: 'SS' },
  // Right side - flat (CB2)
  { zone: { cx: 90, cy: 55, rx: 8, ry: 6, color: FLAT, opacity: 1 }, playerColor: FLAT_LINE, getStart: (c, f) => getCB2StartPosition(c, f), tag: 'CB2' },
  // Right side - curl/flat (DB/LB2)
  { zone: { cx: 70, cy: 52, rx: 10, ry: 7, color: HOOK, opacity: 1 }, playerColor: HOOK_LINE, getStart: (c, f) => getDBLB2StartPosition(c, f), tag: 'DB2' },
  // Middle - hook zone (LB)
  { zone: { cx: 50, cy: 50, rx: 12, ry: 8, color: HOOK, opacity: 1 }, playerColor: HOOK_LINE, getStart: (c, f) => getLBStartPosition(c, f), tag: 'LB' },
];

interface PlayArtProps {
  selectedRoute: RouteType;
  selectedRoute2: RouteType;
  selectedRoute3: RouteType;
  selectedRoute4: RouteType;
  selectedRBRoute: RBRouteType;
  selectedCoverage: CoverageType;
  selectedFormation?: FormationType;
  isPlaying: boolean;
  mode: 'defense' | 'offense' | 'both';
  /** When present, drives defensive rendering instead of the built-in `selectedCoverage`
   *  connector blocks — one Position_Assignment per Defense_Position (Req 8.1, 8.2). */
  customDefenseAssignments?: CustomDefenseAssignments;
}

/** Maps each Defense_Position to its own start-position accessor — the same accessors
 *  already used per-tag inside the built-in `*_CONNECTORS` arrays above. */
const DEFENSE_POSITION_START_GETTERS: Record<
  DefensePositionId,
  (coverage: CoverageType, formation: FormationType) => { x: number; y: number }
> = {
  cb1: getCB1StartPosition,
  cb2: getCB2StartPosition,
  dblb1: getDBLB1StartPosition,
  dblb2: getDBLB2StartPosition,
  fs: getFSStartPosition,
  ss: getSSStartPosition,
  lb: getLBStartPosition,
};

/** Which `COVER4_CONNECTORS` entry (by `.tag`) holds the zone oval for a given
 *  Defense_Position. Every cover4Match option is implemented now (see positionAssignments.ts);
 *  cb1/cb2/fs/ss (deep quarters), dblb1 (curl-flat) and lb (hook) reuse their matching oval.
 *  dblb2 is implemented too but has no dedicated cover4 oval here, so it renders a plain dot. */
const DEFENSE_POSITION_COVER4_TAG: Partial<Record<DefensePositionId, string>> = {
  cb1: 'CB1',
  fs: 'FS',
  ss: 'SS',
  cb2: 'CB2',
  dblb1: 'DB1',
  lb: 'LB',
};

/** Which `COVER4_CONNECTORS` entry (by `.tag`) holds the zone oval for a Cover 4 Palms position.
 *  Palms is a Cover 4 shell, so it reuses the same quarter/curl-flat/hook ovals as cover4Match;
 *  dblb2 has no dedicated Cover 4 oval here, so it renders a plain dot (same as cover4Match). */
const DEFENSE_POSITION_COVER4PALMS_TAG: Partial<Record<DefensePositionId, string>> = {
  cb1: 'CB1',
  fs: 'FS',
  ss: 'SS',
  cb2: 'CB2',
  dblb1: 'DB1',
  lb: 'LB',
};

/** Which `COVER3_CONNECTORS` entry (by `.tag`) holds the zone oval for each Defense_Position.
 *  Every position is implemented for cover3Match (doubles), so all seven map to a zone. */
const DEFENSE_POSITION_COVER3_TAG: Record<DefensePositionId, string> = {
  cb1: 'CB1',
  cb2: 'CB2',
  fs: 'FS',
  dblb1: 'DB1',
  ss: 'SS',
  lb: 'LB',
  dblb2: 'DB2',
};

/** Which `COVER6_CONNECTORS` entry (by `.tag`) holds the zone oval for each Defense_Position.
 *  Every position is implemented for cover6 (both formations), so all seven map to a zone. */
const DEFENSE_POSITION_COVER6_TAG: Record<DefensePositionId, string> = {
  cb1: 'CB1',
  fs: 'FS',
  dblb1: 'DB1',
  cb2: 'CB2',
  ss: 'SS',
  dblb2: 'DB2',
  lb: 'LB',
};

/** Dot color per Defense_Position when no zone oval applies — mirrors the role coloring
 *  (deep/curl-flat/hook) already used for that tag across the built-in connector arrays. */
const DEFENSE_POSITION_DOT_COLOR: Record<DefensePositionId, string> = {
  cb1: DEEP_LINE,
  fs: DEEP_LINE,
  ss: DEEP_LINE,
  cb2: DEEP_LINE,
  dblb1: CURL_FLAT_LINE,
  dblb2: CURL_FLAT_LINE,
  lb: HOOK_LINE,
};

/**
 * Renders one connector/dot per Defense_Position from its OWN assignment, unlike
 * `renderConnectors` which assumes every connector shares a single whole-team `coverage`.
 * A `cover4Match` assignment reuses the matching quarter-zone oval from `COVER4_CONNECTORS`;
 * a `man` assignment (or any assignment with no matching oval, e.g. a placeholder
 * `cover4Match` on dblb1/dblb2/lb) renders as a plain start-position dot with no zone/connector.
 */
const renderCustomDefenseConnectors = (
  assignments: CustomDefenseAssignments,
  formation: FormationType
) => (
  <>
    {DEFENSE_POSITION_IDS.map((position) => {
      const coverage = assignments[position];
      const start = DEFENSE_POSITION_START_GETTERS[position](coverage, formation);

      // Reuse the built-in zone oval for the assigned coverage, if one applies to this position.
      let matched: ZoneConnector | undefined;
      if (coverage === 'cover4Match') {
        const tag = DEFENSE_POSITION_COVER4_TAG[position];
        matched = tag ? COVER4_CONNECTORS.find((conn) => conn.tag === tag) : undefined;
      } else if (coverage === 'cover3Match') {
        const tag = DEFENSE_POSITION_COVER3_TAG[position];
        matched = tag ? COVER3_CONNECTORS.find((conn) => conn.tag === tag) : undefined;
      } else if (coverage === 'cover6') {
        const tag = DEFENSE_POSITION_COVER6_TAG[position];
        matched = tag ? COVER6_CONNECTORS.find((conn) => conn.tag === tag) : undefined;
      } else if (coverage === 'cover4Palms') {
        // Cover 4 Palms is a Cover 4 shell — reuse the Cover 4 quarter/curl-flat/hook ovals.
        const tag = DEFENSE_POSITION_COVER4PALMS_TAG[position];
        matched = tag ? COVER4_CONNECTORS.find((conn) => conn.tag === tag) : undefined;
      }
      if (matched) {
        const { zone, playerColor } = matched;
        return (
          <g key={`custom-conn-${position}`}>
            <ellipse
              cx={zone.cx}
              cy={zone.cy}
              rx={zone.rx}
              ry={zone.ry}
              fill={zone.color}
              opacity={zone.opacity}
              className={`zone-oval ${zone.label ? 'zone-deep' : 'zone-underneath'}`}
            />
            <line
              x1={start.x}
              y1={start.y}
              x2={zone.cx}
              y2={zone.cy}
              className="zone-connector"
              stroke={playerColor}
            />
          </g>
        );
      }

      return (
        <g key={`custom-conn-${position}`}>
          <circle
            cx={start.x}
            cy={start.y}
            r={1.2}
            fill={DEFENSE_POSITION_DOT_COLOR[position]}
            opacity={0.8}
            className="custom-defense-dot"
          />
        </g>
      );
    })}
  </>
);

const generateRoutePath = (
  waypoints: Waypoint[],
  offsetX: number = 0,
  offsetY: number = 0,
  mirror: boolean = false
): string => {
  if (waypoints.length < 2) return '';
  const points = waypoints.map(wp => ({
    x: mirror ? 100 - wp.position.x + offsetX : wp.position.x + offsetX,
    y: wp.position.y + offsetY,
  }));
  let path = `M ${points[0].x} ${points[0].y}`;
  for (let i = 1; i < points.length; i++) {
    path += ` L ${points[i].x} ${points[i].y}`;
  }
  return path;
};

const getRouteEndpoints = (
  waypoints: Waypoint[],
  offsetX: number = 0,
  offsetY: number = 0,
  mirror: boolean = false
) => {
  if (waypoints.length < 2) return null;
  const start = waypoints[0];
  return {
    startX: mirror ? 100 - start.position.x + offsetX : start.position.x + offsetX,
    startY: start.position.y + offsetY,
  };
};

/**
 * Renders zone ovals with connector lines from the defender's start position
 */
const renderConnectors = (connectors: ZoneConnector[], coverage: CoverageType, formation: FormationType = 'doubles') => (
  <>
    {connectors.map((conn, i) => {
      const start = conn.getStart(coverage, formation);
      const { zone, playerColor, tag } = conn;
      return (
        <g key={`conn-${tag}-${i}`}>
          {/* Zone oval tinted to player color */}
          <ellipse
            cx={zone.cx}
            cy={zone.cy}
            rx={zone.rx}
            ry={zone.ry}
            fill={zone.color}
            opacity={zone.opacity}
            className={`zone-oval ${zone.label ? 'zone-deep' : 'zone-underneath'}`}
          />
          {/* Connector line from player to zone center */}
          <line
            x1={start.x}
            y1={start.y}
            x2={zone.cx}
            y2={zone.cy}
            className="zone-connector"
            stroke={playerColor}
          />
        </g>
      );
    })}
  </>
);

const PlayArt: React.FC<PlayArtProps> = ({
  selectedRoute,
  selectedRoute2,
  selectedRoute3,
  selectedRoute4,
  selectedRBRoute,
  selectedCoverage,
  selectedFormation = 'doubles',
  isPlaying,
  mode,
  customDefenseAssignments,
}) => {
  if (isPlaying) return null;

  const showDefense = mode === 'defense' || mode === 'both';
  const showOffense = mode === 'offense' || mode === 'both';
  const isTrips = selectedFormation === 'trips';

  // Derive the custom-defense connectors up front, in a try/catch, so a failure (e.g. a
  // position's start-position accessor throwing) hides only the defensive play art (Req 8.3)
  // without affecting the offensive route layer computed below.
  let customDefenseConnectors: React.ReactNode = null;
  let defenseRenderFailed = false;
  if (customDefenseAssignments) {
    try {
      customDefenseConnectors = renderCustomDefenseConnectors(customDefenseAssignments, selectedFormation);
    } catch {
      defenseRenderFailed = true;
    }
  }

  const wr1Waypoints = getRouteWaypoints(selectedRoute);
  const wr2Waypoints = getRouteWaypoints(selectedRoute2);
  const wr3Waypoints = getRouteWaypoints(selectedRoute3);
  const wr4Waypoints = getRouteWaypoints(selectedRoute4);
  const rbWaypoints = getRBRouteWaypoints(selectedRBRoute);

  const wr1Path = generateRoutePath(wr1Waypoints);
  const wr2Path = generateRoutePath(wr2Waypoints, 10, 5);
  const wr3Path = isTrips
    ? generateRoutePath(wr3Waypoints, 20, 5, false)
    : generateRoutePath(wr3Waypoints, -10, 5, true);
  const wr4Path = generateRoutePath(wr4Waypoints, 0, 0, true);
  const rbPath = generateRoutePath(rbWaypoints);

  const isCover4 = selectedCoverage === 'cover4Match' || selectedCoverage === 'cover4Palms';
  const isCover3 = selectedCoverage === 'cover3Match' || selectedCoverage === 'cover3';
  const isCover2 = selectedCoverage === 'cover2';
  const isCover6 = selectedCoverage === 'cover6';
  const isMan = selectedCoverage === 'man';

  const routes = [
    { path: wr1Path, ep: getRouteEndpoints(wr1Waypoints), color: 'var(--color-off-wr1)', id: 'wr1' },
    { path: wr2Path, ep: getRouteEndpoints(wr2Waypoints, 10, 5), color: 'var(--color-off-wr2)', id: 'wr2' },
    { path: wr3Path, ep: isTrips
        ? getRouteEndpoints(wr3Waypoints, 20, 5, false)
        : getRouteEndpoints(wr3Waypoints, -10, 5, true), color: 'var(--color-off-wr3)', id: 'wr3' },
    { path: wr4Path, ep: getRouteEndpoints(wr4Waypoints, 0, 0, true), color: 'var(--color-off-wr4)', id: 'wr4' },
    { path: rbPath, ep: getRouteEndpoints(rbWaypoints), color: 'var(--color-off-rb)', id: 'rb' },
  ];

  return (
    <svg
      className="play-art"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      data-testid="play-art"
      // Marked as an image with a name rather than left as an unlabelled <svg>, which assistive
      // tech skips entirely. The routes and zones it draws are also stated in text — the route
      // controls name every route, and the player tooltips describe each defender's job — so this
      // names the diagram rather than trying to narrate it.
      role="img"
      aria-label="Play diagram: route lines and coverage zones drawn over the field"
    >
      <defs>
        {routes.map(route => (
          <marker
            key={`arrow-${route.id}`}
            id={`arrow-${route.id}`}
            markerWidth="4"
            markerHeight="4"
            refX="3"
            refY="2"
            orient="auto"
            markerUnits="strokeWidth"
          >
            <path d="M0,0 L0,4 L4,2 z" fill={route.color} opacity="0.9" />
          </marker>
        ))}
      </defs>

      {/* Defensive coverage zones with connector lines */}
      {showDefense && customDefenseAssignments && !defenseRenderFailed && (
        <g className="zone-coverage">
          {customDefenseConnectors}
        </g>
      )}
      {showDefense && !customDefenseAssignments && isCover4 && (
        <g className="zone-coverage">
          {renderConnectors(COVER4_CONNECTORS, selectedCoverage, selectedFormation)}
        </g>
      )}
      {showDefense && !customDefenseAssignments && isCover3 && (
        <g className="zone-coverage">
          {renderConnectors(COVER3_CONNECTORS, selectedCoverage, selectedFormation)}
        </g>
      )}
      {showDefense && !customDefenseAssignments && isCover2 && (
        <g className="zone-coverage">
          {renderConnectors(COVER2_CONNECTORS, selectedCoverage, selectedFormation)}
        </g>
      )}
      {showDefense && !customDefenseAssignments && isCover6 && (
        <g className="zone-coverage">
          {renderConnectors(COVER6_CONNECTORS, selectedCoverage, selectedFormation)}
        </g>
      )}
      {showDefense && !customDefenseAssignments && isMan && (
        <g className="zone-coverage man-coverage">
          {renderConnectors(MAN_CONNECTORS, selectedCoverage, selectedFormation)}
        </g>
      )}

      {/* Offensive route lines */}
      {showOffense && (
        <g className="offensive-routes">
          {routes.map(route => (
            <g key={route.id}>
              <path
                d={route.path}
                className={`route-line route-${route.id}`}
                markerEnd={`url(#arrow-${route.id})`}
              />
              {route.ep && (
                <circle
                  cx={route.ep.startX}
                  cy={route.ep.startY}
                  r="1"
                  fill={route.color}
                  opacity="0.7"
                  className="route-start"
                />
              )}
            </g>
          ))}
        </g>
      )}
    </svg>
  );
};

export default PlayArt;
